import type { Nullable } from '@src/shared/types/common';

import type { SchedulerSavedState } from '../types/modules/kendo-scheduler';
import type { IQueryParams } from '../types/utils/html-query-url';

export class QueryParams {
    public get(): IQueryParams {
        try {
            const regex = /IsFilter.+/g;
            const query = location.search.substring(1);
            const newQuery = query.search(regex) === -1 ? query : (query.match(regex)?.[0] ?? '');

            const ampSeparatedParams = newQuery.split('&');
            const [marker, encoded, ...rest] = ampSeparatedParams;

            if (marker === 'IsFilter=0' && encoded) {
                const decoded = this.decodeParam(encoded);
                const decodedParams = this.extractKeyValuePairs(decoded.length ? decoded : [encoded, ...rest]);
                const tailParams = decoded.length ? this.extractKeyValuePairs(rest) : {};
                return { ...decodedParams, ...tailParams };
            }

            return this.extractKeyValuePairs(ampSeparatedParams);
        }
        catch {
            return {};
        }
    }

    private decodeUrlChars(value: string): string {
        return value.replaceAll(/%22/g, '"')
            .replaceAll(/%7B/g, '{')
            .replaceAll(/%7D/g, '}');
    }

    public getSettings(): Nullable<GridSettingsInUrl> {
        const params = this.get();
        const json = params['settings'];

        try {
            return JSON.parse(this.decodeUrlChars(json));
        }
        catch {
            return null;
        }
    }

    public getSchedulerSettings(): Nullable<SchedulerSavedState> {
        const params = this.get();
        const json = params['schedulerSettings'];

        try {
            return JSON.parse(this.decodeUrlChars(json));
        }
        catch {
            return null;
        }
    }

    public getId(): number | undefined {
        const params = this.get();
        const param = params['Id'];
        if (param) {
            return parseInt(param);
        }
        return undefined;
    }

    private decodeParam(encodedParam: string): string[] {
        const base64Regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;

        if (base64Regex.test(encodedParam)) {
            const originQuery = window.decompressFromBase64(encodedParam);
            return originQuery.split('&');
        }

        return [];
    }

    private extractKeyValuePairs(vars: string[]): IQueryParams {
        const result: IQueryParams = {};

        for (const param of vars) {
            const pair = param.split('=');

            if (pair.length === 2 && pair[1] !== '') {
                result[pair[0]] = pair[1];
            }
        }

        return result;
    }
}
